import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/TestAuthContext';
import AppLayout from '../components/AppLayout';
import { useBranding } from '../contexts/BrandingContext';
import { api, showError } from '../utils/api';
import PendingAISuggestionsWidget from '../components/dashboard/PendingAISuggestionsWidget';
import SavedWorkCard from '../components/dashboard/SavedWorkCard';
import NowLineHeader, { NowLineStats } from '../components/NowLineHeader';
import { AutomationStatusCard } from '../components/automation';
import { AgentActivityPanel } from '../components/pipeline';
import {
  FileText,
  Clock,
  CheckCircle,
  AlertTriangle,
  DollarSign,
  Search,
  Filter,
  MoreHorizontal,
  TrendingUp,
  TrendingDown,
  ChevronDown,
  ChevronRight,
  Eye,
  Download,
  Trash2,
  Share2,
  RefreshCw,
  Upload,
  Briefcase,
  Scale,
  Send,
  Map,
  Users,
  Gavel
} from 'lucide-react';
import { usePermissions, Permissions } from '../contexts/PermissionsContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

interface Report {
  id: string;
  client_name: string;
  business_type?: string;
  event_type?: string;
  status: string;
  law_firm?: string;
  result?: {
    final_claim_amount?: number;
    lost_revenue?: number;
  };
  created_at: string;
  updated_at?: string;
}

export default function DashboardNew() {
  const navigate = useNavigate();
  const location = useLocation();
  const { getToken } = useAuth();
  const { branding } = useBranding();
  const { hasPermission } = usePermissions();
  
  // Check if user can release to attorney (admin or owner has team:change_roles or claims:delete)
  const canReleaseToAttorney = hasPermission(Permissions.TEAM_CHANGE_ROLES) || hasPermission(Permissions.CLAIMS_DELETE);
  
  // Check if user is owner/admin (can view extraction costs)
  const canViewExtractionCosts = hasPermission(Permissions.CLAIMS_DELETE) || hasPermission(Permissions.TEAM_CHANGE_ROLES);
  
  // Read filter from URL query params
  const urlParams = new URLSearchParams(location.search);
  const urlFilter = urlParams.get('filter');
  
  const [reports, setReports] = useState<Report[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState(() => {
    // Map URL filter to status filter value
    if (urlFilter === 'pending') return 'draft';
    if (urlFilter === 'all') return 'all';
    return 'all';
  });
  const [lawFirmFilter, setLawFirmFilter] = useState('all');
  const [lawFirms, setLawFirms] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState('date_desc');
  const [timeRange, setTimeRange] = useState('all');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  
  // Cleanup jobs
  const [savedCleanupJobs, setSavedCleanupJobs] = useState<any[]>([]);
  const [inProgressJobs, setInProgressJobs] = useState<any[]>([]);
  const [selectedJobs, setSelectedJobs] = useState<Set<string>>(new Set());
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [stuckJobIds, setStuckJobIds] = useState<Set<string>>(new Set());
  
  // Report actions menu state
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [showReportDeleteConfirm, setShowReportDeleteConfirm] = useState<string | null>(null);
  const [isDeletingReport, setIsDeletingReport] = useState(false);
  
  // Saved work delete state
  const [showDraftDeleteConfirm, setShowDraftDeleteConfirm] = useState<string | null>(null);
  const [isDeletingDraft, setIsDeletingDraft] = useState(false);

  // Release to attorney state
  const [showReleaseModal, setShowReleaseModal] = useState<string | null>(null);
  const [lawFirmsList, setLawFirmsList] = useState<{id: string; name: string; contact_email?: string; attorney_password_set?: boolean}[]>([]);
  const [selectedLawFirm, setSelectedLawFirm] = useState<string>('');
  const [isReleasing, setIsReleasing] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);

  // Update filter when URL changes
  useEffect(() => {
    if (urlFilter === 'pending') {
      setStatusFilter('draft');
    } else if (urlFilter === 'all' || !urlFilter) {
      setStatusFilter('all');
    }
  }, [urlFilter]);

  // Stats
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    completed: 0,
    totalValue: 0,
    trend: '+12%'
  });

  // Queue stats for dashboard summary (P0/P1)
  const [queueStats, setQueueStats] = useState({
    available: 0,
    myClaims: 0,
    readyForAttorney: 0,
    needsReview: 0,
    attorneyCases: 0
  });

  // Now-Line stats for Phase A UX enhancement
  const [nowLineStats, setNowLineStats] = useState<NowLineStats>({
    needsAttention: { count: 0, breakdown: [] },
    noActionNeeded: { count: 0, breakdown: [] }
  });
  const [nowLineLoading, setNowLineLoading] = useState(true);

  useEffect(() => {
    fetchReports();
    fetchAllCleanupJobs();
    fetchStuckJobs();
    fetchLawFirms();
    fetchQueueStats();
    fetchNowLineStats();
  }, []);

  // Fetch law firms for filter dropdown
  const fetchLawFirms = async () => {
    try {
      const token = await getToken();
      const data = await api.get('/api/v1/law-firms', {
        headers: { Authorization: `Bearer ${token}` },
        showErrorToast: false
      });
      const firmNames = (data.law_firms || []).map((f: any) => f.name);
      setLawFirms(firmNames);
    } catch (error) {
      console.error('Error fetching law firms:', error);
    }
  };

  // Fetch queue stats for dashboard summary (P0/P1)
  const fetchQueueStats = async () => {
    try {
      const token = await getToken();
      const data = await api.get('/api/v1/queue/accountant/stats', {
        headers: { Authorization: `Bearer ${token}` },
        showErrorToast: false
      });
      // Also fetch general queue stats for attorney count
      let attorneyCount = 0;
      try {
        const generalStats = await api.get('/api/v1/queue/stats?user_role=attorney', {
          headers: { Authorization: `Bearer ${token}` },
          showErrorToast: false
        });
        attorneyCount = generalStats.attorney || 0;
      } catch {
        // Attorney stats optional
      }
      setQueueStats({
        available: data.available_count || 0,
        myClaims: data.my_claims_count || 0,
        readyForAttorney: data.ready_count || 0,
        needsReview: data.needs_review_count || 0,
        attorneyCases: attorneyCount
      });
    } catch (error) {
      // Silently ignore - queue stats are optional enhancement
      console.debug('Queue stats not available:', error);
    }
  };

  // Fetch Now-Line stats for Phase A UX enhancement
  const fetchNowLineStats = async () => {
    setNowLineLoading(true);
    try {
      const token = await getToken();
      const data = await api.get('/api/v1/queue/nowline-stats?role=data_specialist', {
        headers: { Authorization: `Bearer ${token}` },
        showErrorToast: false
      });
      setNowLineStats({
        needsAttention: {
          count: data.needs_attention?.count || 0,
          breakdown: data.needs_attention?.breakdown || []
        },
        noActionNeeded: {
          count: data.no_action_needed?.count || 0,
          breakdown: data.no_action_needed?.breakdown || []
        }
      });
    } catch (error) {
      console.debug('Now-Line stats not available:', error);
    } finally {
      setNowLineLoading(false);
    }
  };

  // Fetch stuck jobs
  const fetchStuckJobs = async () => {
    try {
      const token = await getToken();
      const data = await api.get('/api/v1/cleanup/jobs/stuck', {
        headers: { Authorization: `Bearer ${token}` },
        showErrorToast: false
      });
      const stuckIds = new Set(data.stuck_jobs?.map((j: any) => j.id) || []);
      setStuckJobIds(stuckIds as Set<string>);
      // Silently ignore 404 - endpoint may not exist
    } catch (error) {
      // Silently ignore - stuck jobs check is optional
    }
  };

  // Check if a job is stuck (in processing state for >30 minutes)
  const isJobStuck = (job: any): boolean => {
    // First check if backend marked it as stuck
    if (stuckJobIds.has(job.id)) return true;
    
    // Also do client-side check for immediate feedback
    const processingStates = ['pending', 'extracting', 'classifying', 'processing'];
    if (!processingStates.includes(job.status)) return false;
    
    const createdAt = new Date(job.created_at);
    const now = new Date();
    const minutesElapsed = (now.getTime() - createdAt.getTime()) / (1000 * 60);
    
    return minutesElapsed > 30;
  };

  // Get stuck duration in human readable format
  const getStuckDuration = (job: any): string => {
    const createdAt = new Date(job.created_at);
    const now = new Date();
    const minutesElapsed = Math.floor((now.getTime() - createdAt.getTime()) / (1000 * 60));
    
    if (minutesElapsed < 60) return `${minutesElapsed} min`;
    const hours = Math.floor(minutesElapsed / 60);
    if (hours < 24) return `${hours}h ${minutesElapsed % 60}m`;
    const days = Math.floor(hours / 24);
    return `${days}d ${hours % 24}h`;
  };

  const fetchAllCleanupJobs = async () => {
    try {
      const token = await getToken();
      
      // Fetch wizard drafts with enhanced metadata
      const drafts = await api.get('/api/v1/reports/drafts/list', {
        headers: { Authorization: `Bearer ${token}` },
        showErrorToast: false
      });
      
      // Map drafts to enhanced saved work items
      const draftJobs = (drafts || []).map((d: any) => {
        return {
          id: d.draft_id || d.id,
          business_name: d.client_name || d.data?.client_name || d.data?.business_name || 'Unnamed Claim',
          client_name: d.client_name || d.data?.client_name || d.data?.business_name || 'Unnamed Claim',
          law_firm: d.law_firm || d.data?.law_firm || '',
          bank_name: 'Claim Wizard',
          status: 'draft',
          workflow_status: d.workflow_status,
          auto_status: d.auto_status,
          priority: d.priority || 'medium',
          notes: d.notes || '',
          saved_for_later: true,
          saved_at: d.updated_at || d.created_at,
          created_at: d.created_at,
          updated_at: d.updated_at,
          days_inactive: d.days_inactive || 0,
          doc_count: d.doc_count || 0,
          total_transactions: d.total_transactions || 0,
          needs_review: d.needs_review || 0,
          stats: {
            total_transactions: d.total_transactions || 0,
            auto_approved: (d.total_transactions || 0) - (d.needs_review || 0),
            needs_review: d.needs_review || 0
          },
          source: 'wizard',
          step: d.step
        };
      });
      
      // All wizard drafts go to saved work
      setSavedCleanupJobs(draftJobs);
      setInProgressJobs([]);
    } catch (error) {
      console.error('Error fetching saved work:', error);
    }
  };

  const fetchReports = async () => {
    setLoading(true);
    try {
      const token = await getToken();
      // Request up to 200 reports to show all user's claims
      const data = await api.get('/api/v1/reports?limit=200', {
        headers: { Authorization: `Bearer ${token}` }
      });
      // API returns { reports: [...], total: N }
      const reportsList = data.reports || data;
      setReports(reportsList);
      
      // Calculate stats
      const total = reportsList.length;
      const pending = reportsList.filter((r: Report) => r.status === 'in_progress' || r.status === 'draft').length;
      const completed = reportsList.filter((r: Report) => r.status === 'completed').length;
      const totalValue = reportsList.reduce((sum: number, r: Report) => 
        sum + (r.result?.final_claim_amount || 0), 0
      );
      
      setStats({ total, pending, completed, totalValue, trend: '+12%' });
    } catch (error) {
      console.error('Error fetching reports:', error);
    } finally {
      setLoading(false);
    }
  };

  // Toggle job selection for bulk delete
  const toggleJobSelection = (jobId: string) => {
    setSelectedJobs(prev => {
      const newSet = new Set(prev);
      if (newSet.has(jobId)) {
        newSet.delete(jobId);
      } else {
        newSet.add(jobId);
      }
      return newSet;
    });
  };

  // Select all in-progress jobs
  const selectAllInProgress = () => {
    if (selectedJobs.size === inProgressJobs.length) {
      setSelectedJobs(new Set());
    } else {
      setSelectedJobs(new Set(inProgressJobs.map(j => j.id)));
    }
  };

  // Delete a single job
  const deleteJob = async (jobId: string) => {
    try {
      const token = await getToken();
      await api.delete(`/api/v1/cleanup/jobs/${jobId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Remove from local state
      setInProgressJobs(prev => prev.filter(j => j.id !== jobId));
      setSavedCleanupJobs(prev => prev.filter(j => j.id !== jobId));
      setSelectedJobs(prev => {
        const newSet = new Set(prev);
        newSet.delete(jobId);
        return newSet;
      });
    } catch (error) {
      console.error('Error deleting job:', error);
    }
  };

  // Bulk delete selected jobs
  const bulkDeleteJobs = async () => {
    if (selectedJobs.size === 0) return;
    
    setIsDeleting(true);
    try {
      const token = await getToken();
      const result = await api.post('/api/v1/cleanup/jobs/bulk-delete', 
        { job_ids: Array.from(selectedJobs) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Remove deleted jobs from local state
      setInProgressJobs(prev => prev.filter(j => !selectedJobs.has(j.id)));
      setSavedCleanupJobs(prev => prev.filter(j => !selectedJobs.has(j.id)));
      setSelectedJobs(new Set());
      setShowDeleteConfirm(false);
      
      // Show success message
      setStatusMessage(`Deleted ${result.deleted_count} jobs (${result.total_transactions_deleted} transactions)`);
    } catch (error) {
      console.error('Error bulk deleting jobs:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  // Delete a single report
  const deleteReport = async (reportId: string) => {
    setIsDeletingReport(true);
    try {
      const token = await getToken();
      await api.delete(`/api/v1/reports/${reportId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Remove from local state
      setReports(prev => prev.filter(r => r.id !== reportId));
      setShowReportDeleteConfirm(null);
      setOpenMenuId(null);
      
      // Update stats
      setStats(prev => ({
        ...prev,
        total: prev.total - 1,
        completed: reports.find(r => r.id === reportId)?.status === 'completed' 
          ? prev.completed - 1 
          : prev.completed
      }));
    } catch (error) {
      console.error('Error deleting report:', error);
    } finally {
      setIsDeletingReport(false);
    }
  };

  // Fetch law firms for release modal
  const fetchLawFirmsForRelease = async () => {
    try {
      const token = await getToken();
      const data = await api.get('/api/v1/law-firms', {
        headers: { Authorization: `Bearer ${token}` },
        showErrorToast: false
      });
      setLawFirmsList(data.law_firms || []);
    } catch (error) {
      console.error('Error fetching law firms:', error);
    }
  };

  // Release case to attorney
  const releaseToAttorney = async (caseId: string) => {
    if (!selectedLawFirm) {
      setValidationError('Please select a law firm');
      return;
    }

    setIsReleasing(true);
    setValidationError(null);
    try {
      const token = await getToken();
      const result = await api.post(
        `/api/v1/attorney/admin/cases/${caseId}/release`,
        { law_firm_id: selectedLawFirm },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setStatusMessage(result.message || 'Case released to attorney successfully!');
      setShowReleaseModal(null);
      setSelectedLawFirm('');
    } catch (error: any) {
      console.error('Error releasing to attorney:', error);
      setValidationError(error.message || 'Failed to release case');
    } finally {
      setIsReleasing(false);
    }
  };

  // Open release modal
  const openReleaseModal = async (reportId: string) => {
    setShowReleaseModal(reportId);
    setOpenMenuId(null);
    await fetchLawFirmsForRelease();
  };

  // Delete a saved draft
  const deleteDraft = async (draftId: string) => {
    setIsDeletingDraft(true);
    try {
      const token = await getToken();
      await api.delete(`/api/v1/reports/drafts/${draftId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Remove from local state
      setSavedCleanupJobs(prev => prev.filter(j => j.id !== draftId));
      setShowDraftDeleteConfirm(null);
    } catch (error) {
      console.error('Error deleting draft:', error);
    } finally {
      setIsDeletingDraft(false);
    }
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setOpenMenuId(null);
    if (openMenuId) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [openMenuId]);

  const filteredReports = useMemo(() => {
    let filtered = [...reports];

    // Time range filter
    if (timeRange !== 'all') {
      const days = parseInt(timeRange);
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - days);
      filtered = filtered.filter(r => new Date(r.created_at) >= cutoffDate);
    }

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(r => 
        r.client_name?.toLowerCase().includes(query) ||
        r.business_type?.toLowerCase().includes(query) ||
        r.event_type?.toLowerCase().includes(query) ||
        r.id?.toLowerCase().includes(query) ||
        r.law_firm?.toLowerCase().includes(query)
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(r => r.status === statusFilter);
    }

    // Law Firm filter
    if (lawFirmFilter !== 'all') {
      filtered = filtered.filter(r => r.law_firm === lawFirmFilter);
    }

    // Sort
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'date_desc':
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        case 'date_asc':
          return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
        case 'name':
          return (a.client_name || '').localeCompare(b.client_name || '');
        case 'amount':
          return (b.result?.final_claim_amount || 0) - (a.result?.final_claim_amount || 0);
        default:
          return 0;
      }
    });

    return filtered;
  }, [reports, searchQuery, statusFilter, lawFirmFilter, sortBy, timeRange]);

  // Pagination calculations
  const totalPages = Math.ceil(filteredReports.length / itemsPerPage);
  const paginatedReports = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredReports.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredReports, currentPage, itemsPerPage]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, statusFilter, lawFirmFilter, sortBy, timeRange]);

  const formatCurrency = (amount: number) => {
    if (amount >= 1000000) {
      return `$${(amount / 1000000).toFixed(1)}M`;
    } else if (amount >= 1000) {
      return `$${(amount / 1000).toFixed(0)}K`;
    }
    return `$${amount.toFixed(0)}`;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const getStatusBadge = (status: string) => {
    const configs: Record<string, { bg: string; text: string; icon: React.ElementType; label: string }> = {
      completed: { bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle, label: 'Complete' },
      in_progress: { bg: 'bg-blue-100', text: 'text-blue-700', icon: Clock, label: 'In Progress' },
      processing: { bg: 'bg-blue-100', text: 'text-blue-700', icon: Clock, label: 'Processing' },
      draft: { bg: 'bg-amber-100', text: 'text-amber-700', icon: Clock, label: 'Pending Review' },
      review: { bg: 'bg-yellow-100', text: 'text-yellow-700', icon: AlertTriangle, label: 'Needs Review' },
      causation_failed: { bg: 'bg-red-100', text: 'text-red-700', icon: AlertTriangle, label: 'Causation Failed' },
      failed: { bg: 'bg-red-100', text: 'text-red-700', icon: AlertTriangle, label: 'Failed' },
    };
    const config = configs[status] || { bg: 'bg-gray-100', text: 'text-gray-600', icon: FileText, label: status };
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </span>
    );
  };

  return (
    <AppLayout>
      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" role="dialog" aria-modal="true" aria-labelledby="delete-jobs-title">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <Trash2 className="w-6 h-6 text-red-600" aria-hidden="true" />
              </div>
              <div>
                <h3 id="delete-jobs-title" className="text-lg font-bold text-gray-900">Delete {selectedJobs.size} Jobs?</h3>
                <p className="text-sm text-gray-500">This action cannot be undone</p>
              </div>
            </div>
            <p className="text-gray-600 mb-6">
              This will permanently delete {selectedJobs.size} cleanup job{selectedJobs.size > 1 ? 's' : ''} and all associated transactions and anomalies.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={isDeleting}
                className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={bulkDeleteJobs}
                disabled={isDeleting}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors flex items-center justify-center gap-2"
              >
                {isDeleting ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4" />
                    Delete All
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="p-6">
        {/* Page Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-xl font-bold text-gray-900">Claims Dashboard</h1>
            <p className="text-sm text-gray-500">Manage and track all your business interruption claims</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={fetchReports}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
              title="Refresh"
              aria-label="Refresh claims"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} aria-hidden="true" />
            </button>
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
              aria-label="Filter by time range"
            >
              <option value="7">Last 7 days</option>
              <option value="30">Last 30 days</option>
              <option value="90">Last 90 days</option>
              <option value="365">This year</option>
              <option value="all">All time</option>
            </select>
          </div>
        </div>

        {/* Now-Line Header - Phase A UX Enhancement */}
        <NowLineHeader
          stats={nowLineStats}
          loading={nowLineLoading}
          onViewNeedsAttention={() => setStatusFilter('draft')}
          onViewNoAction={() => setStatusFilter('completed')}
        />

        {/* Automation Status Card - 90% Automation System Visibility */}
        {hasPermission('VIEW_ALL_CLAIMS') && (
          <div className="mb-6">
            <AutomationStatusCard />
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {[
            { 
              icon: FileText, 
              label: 'Total Claims', 
              value: stats.total, 
              trend: stats.trend, 
              trendUp: true,
              color: 'blue',
              filter: 'all',
              clickable: true
            },
            { 
              icon: Clock, 
              label: 'Pending Review', 
              value: stats.pending, 
              trend: stats.pending > 0 ? `${stats.pending} need attention` : 'All caught up',
              trendUp: false,
              color: 'yellow',
              filter: 'draft',
              clickable: true
            },
            { 
              icon: CheckCircle, 
              label: 'Completed', 
              value: stats.completed, 
              trend: `${Math.round((stats.completed / Math.max(stats.total, 1)) * 100)}% completion`,
              trendUp: true,
              color: 'green',
              filter: 'completed',
              clickable: true
            },
            { 
              icon: DollarSign, 
              label: 'Total Value', 
              value: formatCurrency(stats.totalValue), 
              trend: '+18% vs last month',
              trendUp: true,
              color: 'purple',
              filter: null,
              clickable: false
            },
          ].map((stat, i) => (
            stat.clickable ? (
              <button 
                key={i} 
                onClick={() => setStatusFilter(stat.filter!)}
                className={`bg-white rounded-xl border p-5 shadow-sm hover:shadow-md transition-all text-left ${
                  statusFilter === stat.filter ? 'border-blue-500 ring-2 ring-blue-500/20' : 'border-gray-200'
                }`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className={`p-2.5 rounded-lg ${
                    stat.color === 'blue' ? 'bg-blue-100' :
                    stat.color === 'yellow' ? 'bg-yellow-100' :
                    stat.color === 'green' ? 'bg-green-100' :
                    'bg-blue-100'
                  }`}>
                    <stat.icon className={`w-5 h-5 ${
                      stat.color === 'blue' ? 'text-blue-600' :
                      stat.color === 'yellow' ? 'text-yellow-600' :
                      stat.color === 'green' ? 'text-green-600' :
                      'text-blue-600'
                    }`} />
                  </div>
                  {stat.trendUp !== undefined && typeof stat.value === 'number' && (
                    <span className={`flex items-center gap-1 text-xs font-medium px-2 py-1 rounded ${
                      stat.trendUp ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {stat.trendUp ? <TrendingUp className="w-3 h-3" /> : null}
                      {stat.trend}
                    </span>
                  )}
                </div>
                <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                <p className="text-sm text-gray-500 mt-1">{stat.label}</p>
              </button>
            ) : (
              <div 
                key={i} 
                className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="p-2.5 rounded-lg bg-blue-100">
                    <stat.icon className="w-5 h-5 text-blue-600" />
                  </div>
                </div>
                <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                <p className="text-sm text-gray-500 mt-1">{stat.label}</p>
              </div>
            )
          ))}
        </div>

        {/* Pending AI Suggestions Widget */}
        <PendingAISuggestionsWidget />

        {/* AI Agent Activity Panel - Shows real-time pipeline processing */}
        <div className="mb-6">
          <AgentActivityPanel 
            showTimeline={true}
            defaultExpanded={true}
            onAgentClick={(agentId, claimId) => {
              console.log('Agent clicked:', agentId, claimId);
              // Could navigate to specific agent view or claim
            }}
          />
        </div>

        {/* Quick Access to Queue Dashboards - Enhanced with Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <button
            onClick={() => navigate('/queue')}
            className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm hover:shadow-md transition-all text-left"
          >
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-amber-100">
                <FileText className="w-6 h-6 text-amber-600" />
              </div>
              <div className="flex-1">
                <p className="font-semibold text-gray-900">Work Queue</p>
                <p className="text-sm text-gray-500">All work items in one place</p>
              </div>
              {queueStats.needsReview > 0 && (
                <span className="px-2.5 py-1 bg-amber-100 text-amber-700 text-xs font-semibold rounded-full">
                  {queueStats.needsReview} need review
                </span>
              )}
            </div>
          </button>
          <button
            onClick={() => navigate('/queue/accountant')}
            className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm hover:shadow-md transition-all text-left"
          >
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-green-100">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <div className="flex-1">
                <p className="font-semibold text-gray-900">Accountant Queue</p>
                <p className="text-sm text-gray-500">Review & sign off on cases</p>
              </div>
              <div className="flex flex-col items-end gap-1">
                {queueStats.available > 0 && (
                  <span className="px-2.5 py-0.5 bg-blue-100 text-blue-700 text-xs font-semibold rounded-full">
                    {queueStats.available} available
                  </span>
                )}
                {queueStats.myClaims > 0 && (
                  <span className="px-2.5 py-0.5 bg-green-100 text-green-700 text-xs font-semibold rounded-full">
                    {queueStats.myClaims} claimed
                  </span>
                )}
              </div>
            </div>
          </button>
          <button
            onClick={() => navigate('/queue/attorney')}
            className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm hover:shadow-md transition-all text-left"
          >
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-purple-100">
                <Gavel className="w-6 h-6 text-purple-600" />
              </div>
              <div className="flex-1">
                <p className="font-semibold text-gray-900">Attorney Queue</p>
                <p className="text-sm text-gray-500">Attorney-assigned cases</p>
              </div>
              {queueStats.attorneyCases > 0 && (
                <span className="px-2.5 py-1 bg-purple-100 text-purple-700 text-xs font-semibold rounded-full">
                  {queueStats.attorneyCases} cases
                </span>
              )}
            </div>
          </button>
          <button
            onClick={() => navigate('/orchestrator')}
            className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm hover:shadow-md transition-all text-left"
          >
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-blue-100">
                <Scale className="w-6 h-6 text-blue-600" />
              </div>
              <div className="flex-1">
                <p className="font-semibold text-gray-900">Orchestrator</p>
                <p className="text-sm text-gray-500">AI pipeline & automation</p>
              </div>
              {queueStats.readyForAttorney > 0 && (
                <span className="px-2.5 py-1 bg-emerald-100 text-emerald-700 text-xs font-semibold rounded-full">
                  {queueStats.readyForAttorney} ready
                </span>
              )}
            </div>
          </button>
        </div>

        {/* Staff Dashboard Quick Link */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/staff')}
            className="w-full bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl p-4 shadow-sm hover:shadow-md transition-all text-left text-white"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-lg bg-white/20">
                  <Users className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="font-semibold">Staff Dashboard</p>
                  <p className="text-sm text-blue-100">Case management, readiness, extracted docs</p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-white/70" />
            </div>
          </button>
        </div>

        {/* In Progress Jobs Banner - Shows processing jobs and jobs needing review */}
        {inProgressJobs.length > 0 && (
          <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-4 mb-6">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={selectedJobs.size === inProgressJobs.length && inProgressJobs.length > 0}
                  onChange={selectAllInProgress}
                  className="w-4 h-4 rounded border-amber-300 text-amber-600 focus:ring-amber-500"
                  aria-label="Select all in-progress jobs"
                />
                <span className="text-xl">⏳</span>
                <h3 className="font-semibold text-amber-900">In Progress ({inProgressJobs.length})</h3>
              </div>
              <div className="flex items-center gap-2">
                {selectedJobs.size > 0 && (
                  <button
                    onClick={() => setShowDeleteConfirm(true)}
                    className="px-3 py-1.5 text-sm font-medium text-red-700 bg-red-100 hover:bg-red-200 rounded-lg flex items-center gap-1 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                    Delete ({selectedJobs.size})
                  </button>
                )}
                <span className="text-xs text-amber-600 bg-amber-100 px-2 py-1 rounded-full">
                  Needs attention
                </span>
              </div>
            </div>
            <div className="space-y-2">
              {inProgressJobs.map((job) => {
                const isProcessing = ['pending', 'extracting', 'classifying'].includes(job.status);
                const needsReview = (job.stats?.needs_review || 0) > 0;
                const isSelected = selectedJobs.has(job.id);
                const stuck = isJobStuck(job);
                
                return (
                  <div 
                    key={job.id}
                    className={`bg-white rounded-lg border p-3 flex items-center justify-between transition-colors ${
                      stuck ? 'border-red-300 bg-red-50' :
                      isSelected ? 'border-amber-400 bg-amber-50' : 'border-amber-100 hover:border-amber-300'
                    }`}
                  >
                    <div className="flex items-center gap-3 flex-1">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleJobSelection(job.id)}
                        className="w-4 h-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                        aria-label={`Select job ${job.business_name || 'unnamed'}`}
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900">
                            {job.business_name || (job.files?.[0]?.filename?.replace('.pdf', '') || 'Bank Statement Upload')}
                          </span>
                          {stuck && isProcessing && (
                            <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full flex items-center gap-1">
                              <AlertTriangle className="w-3 h-3" />
                              Stuck ({getStuckDuration(job)})
                            </span>
                          )}
                          {!stuck && isProcessing && (
                            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full flex items-center gap-1">
                              <span className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-pulse"></span>
                              {job.status === 'extracting' ? 'Extracting...' : 
                               job.status === 'classifying' ? 'Classifying...' : 'Processing...'}
                            </span>
                          )}
                          {!isProcessing && needsReview && (
                            <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                              {job.stats.needs_review} to review
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-3 mt-1 text-sm text-gray-500">
                          {job.stats?.total_transactions > 0 ? (
                            <>
                              <span>{job.stats.total_transactions} transactions</span>
                              <span>•</span>
                              <span>{job.stats.auto_approved || 0} auto-approved</span>
                              {needsReview && (
                                <>
                                  <span>•</span>
                                  <span className="text-amber-600 font-medium">{job.stats.needs_review} need review</span>
                                </>
                              )}
                            </>
                          ) : (
                            <span>{job.files?.length || 0} files uploaded</span>
                          )}
                          <span>•</span>
                          <span>{job.created_at ? new Date(job.created_at).toLocaleDateString() : 'Recently'}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={(e) => { e.stopPropagation(); deleteJob(job.id); }}
                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete job"
                        aria-label={`Delete job ${job.business_name || 'unnamed'}`}
                      >
                        <Trash2 className="w-4 h-4" aria-hidden="true" />
                      </button>
                      {stuck ? (
                        <button
                          onClick={(e) => { e.stopPropagation(); deleteJob(job.id); }}
                          className="px-4 py-2 text-sm font-medium rounded-lg bg-red-100 text-red-700 hover:bg-red-200 transition-colors"
                        >
                          Delete Stuck Job
                        </button>
                      ) : (
                        <button
                          onClick={() => navigate(`/cleanup/validation/${job.id}`)}
                          className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                            isProcessing 
                              ? 'bg-blue-100 text-blue-700 hover:bg-blue-200' 
                              : 'bg-amber-600 hover:bg-amber-700 text-white'
                          }`}
                        >
                          {isProcessing ? 'View Progress' : 'Review'}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Saved Work Section - Enhanced Cards */}
        {savedCleanupJobs.length > 0 && (
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4 mb-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <span className="text-xl">📋</span>
                <h3 className="font-semibold text-blue-900">Saved Work ({savedCleanupJobs.length})</h3>
              </div>
              <span className="text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded-full">
                Continue where you left off
              </span>
            </div>
            <div className="space-y-3">
              {savedCleanupJobs.map((job) => (
                <SavedWorkCard
                  key={job.id}
                  job={job}
                  onDelete={(id) => setShowDraftDeleteConfirm(id)}
                  onStatusChange={fetchAllCleanupJobs}
                />
              ))}
            </div>
          </div>
        )}

        {/* Draft Delete Confirmation Modal */}
        {showDraftDeleteConfirm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" role="dialog" aria-modal="true" aria-labelledby="delete-draft-title">
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                  <Trash2 className="w-6 h-6 text-red-600" aria-hidden="true" />
                </div>
                <div>
                  <h3 id="delete-draft-title" className="text-lg font-bold text-gray-900">Delete Saved Work?</h3>
                  <p className="text-sm text-gray-500">This action cannot be undone</p>
                </div>
              </div>
              <p className="text-gray-600 mb-6">
                This will permanently delete this saved draft and all associated data. You will need to start over if you want to continue this claim.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowDraftDeleteConfirm(null)}
                  disabled={isDeletingDraft}
                  className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors"
                  data-testid="draft-delete-cancel"
                >
                  Cancel
                </button>
                <button
                  onClick={() => deleteDraft(showDraftDeleteConfirm)}
                  disabled={isDeletingDraft}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors flex items-center justify-center gap-2"
                  data-testid="draft-delete-confirm"
                >
                  {isDeletingDraft ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4" />
                      Delete
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Filter Bar */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" aria-hidden="true" />
              <input
                type="text"
                placeholder="Search by client, business, event type, or report ID..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                aria-label="Search claims by client, business, event type, or report ID"
              />
            </div>
            
            {/* Law Firm Filter */}
            <div className="relative">
              <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" aria-hidden="true" />
              <select
                value={lawFirmFilter}
                onChange={(e) => setLawFirmFilter(e.target.value)}
                className="pl-10 pr-8 py-2.5 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 min-w-[180px] appearance-none"
                aria-label="Filter by law firm"
              >
                <option value="all">All Law Firms</option>
                {lawFirms.map((firm) => (
                  <option key={firm} value={firm}>{firm}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>
            
            {/* Status Filter */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2.5 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 min-w-[150px]"
              aria-label="Filter by status"
            >
              <option value="all">All Status</option>
              <option value="completed">Complete</option>
              <option value="draft">Pending Review</option>
              <option value="in_progress">In Progress</option>
              <option value="causation_failed">Causation Failed</option>
            </select>

            {/* Sort */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-4 py-2.5 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 min-w-[150px]"
              aria-label="Sort claims by"
            >
              <option value="date_desc">Newest First</option>
              <option value="date_asc">Oldest First</option>
              <option value="name">Name A-Z</option>
              <option value="amount">Highest Value</option>
            </select>
          </div>
          
          {/* Active Filters Display */}
          {(lawFirmFilter !== 'all' || statusFilter !== 'all') && (
            <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-100">
              <span className="text-xs text-gray-500">Active filters:</span>
              {lawFirmFilter !== 'all' && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 rounded-full text-xs">
                  <Briefcase className="w-3 h-3" />
                  {lawFirmFilter}
                  <button
                    onClick={() => setLawFirmFilter('all')}
                    className="ml-1 hover:bg-blue-100 rounded-full p-0.5"
                    aria-label={`Remove law firm filter: ${lawFirmFilter}`}
                  >
                    ×
                  </button>
                </span>
              )}
              {statusFilter !== 'all' && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs">
                  {statusFilter}
                  <button
                    onClick={() => setStatusFilter('all')}
                    className="ml-1 hover:bg-gray-200 rounded-full p-0.5"
                    aria-label={`Remove status filter: ${statusFilter}`}
                  >
                    ×
                  </button>
                </span>
              )}
              <button
                onClick={() => {
                  setLawFirmFilter('all');
                  setStatusFilter('all');
                }}
                className="text-xs text-gray-500 hover:text-gray-700 ml-2"
              >
                Clear all
              </button>
            </div>
          )}
        </div>

        {/* Claims Table */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          {/* Table Header */}
          <div className="px-5 py-4 border-b border-gray-200 bg-gray-50">
            <div className="flex items-center justify-between">
              <h2 className="font-semibold text-gray-900">All Claims</h2>
              <span className="text-sm text-gray-500">{filteredReports.length} claims</span>
            </div>
          </div>

          {/* Mobile Card View - shown only on mobile */}
          <div className="md:hidden">
            {loading ? (
              <div className="p-4 space-y-3">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="animate-pulse bg-gray-50 rounded-lg p-4 border border-gray-100">
                    <div className="h-4 bg-gray-200 rounded w-40 mb-2" />
                    <div className="h-3 bg-gray-200 rounded w-24 mb-3" />
                    <div className="flex justify-between">
                      <div className="h-3 bg-gray-200 rounded w-20" />
                      <div className="h-3 bg-gray-200 rounded w-16" />
                    </div>
                  </div>
                ))}
              </div>
            ) : filteredReports.length === 0 ? (
              <div className="px-5 py-12 text-center">
                <div className="flex flex-col items-center">
                  <FileText className="w-12 h-12 text-gray-300 mb-3" />
                  <p className="text-gray-500 font-medium">No claims found</p>
                  <p className="text-sm text-gray-400 mt-1">
                    {searchQuery ? 'Try adjusting your search' : 'Create your first claim to get started'}
                  </p>
                  {!searchQuery && (
                    <button
                      onClick={() => navigate('/wizard-v2')}
                      className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
                    >
                      Create New Claim
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div className="p-4 space-y-3">
                {paginatedReports.map((report) => (
                  <div
                    key={report.id}
                    className="bg-white rounded-lg shadow-sm p-4 border border-gray-200 active:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => navigate(`/report/${report.id}`)}
                    role="link"
                    tabIndex={0}
                    aria-label={`View claim for ${report.client_name || 'Unnamed Claim'}`}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(`/report/${report.id}`); }}}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <FileText className="w-5 h-5 text-blue-600" />
                        </div>
                        <div className="min-w-0">
                          <p className="font-semibold text-gray-900 truncate">{report.client_name || 'Unnamed Claim'}</p>
                          <p className="text-sm text-gray-500 capitalize">
                            {report.event_type || report.business_type || 'N/A'}
                          </p>
                        </div>
                      </div>
                      <div className="flex-shrink-0">
                        {getStatusBadge(report.status)}
                      </div>
                    </div>
                    {report.law_firm && (
                      <div className="mt-2 ml-[52px]">
                        <span className="inline-flex items-center gap-1.5 px-2 py-1 bg-gray-100 text-gray-700 rounded-md text-xs">
                          <Briefcase className="w-3 h-3" />
                          {report.law_firm}
                        </span>
                      </div>
                    )}
                    <div className="flex items-center justify-between mt-3 ml-[52px]">
                      <span className="text-sm text-gray-500">
                        {formatDate(report.created_at)}
                      </span>
                      <span className="text-sm font-semibold text-gray-900">
                        {report.result?.final_claim_amount
                          ? formatCurrency(report.result.final_claim_amount)
                          : '-'
                        }
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Desktop Table - hidden on mobile */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-5 py-3">Claim</th>
                  <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-5 py-3">Law Firm</th>
                  <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-5 py-3">Type</th>
                  <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-5 py-3">Amount</th>
                  <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-5 py-3">Status</th>
                  <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-5 py-3">Date</th>
                  <th className="px-5 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading ? (
                  // Loading skeleton
                  [...Array(5)].map((_, i) => (
                    <tr key={i} className="animate-pulse">
                      <td className="px-5 py-4"><div className="h-4 bg-gray-200 rounded w-32" /></td>
                      <td className="px-5 py-4"><div className="h-4 bg-gray-200 rounded w-20" /></td>
                      <td className="px-5 py-4"><div className="h-4 bg-gray-200 rounded w-16" /></td>
                      <td className="px-5 py-4"><div className="h-4 bg-gray-200 rounded w-24" /></td>
                      <td className="px-5 py-4"><div className="h-4 bg-gray-200 rounded w-20" /></td>
                      <td className="px-5 py-4"><div className="h-4 bg-gray-200 rounded w-8" /></td>
                    </tr>
                  ))
                ) : filteredReports.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-5 py-12 text-center">
                      <div className="flex flex-col items-center">
                        <FileText className="w-12 h-12 text-gray-300 mb-3" />
                        <p className="text-gray-500 font-medium">No claims found</p>
                        <p className="text-sm text-gray-400 mt-1">
                          {searchQuery ? 'Try adjusting your search' : 'Create your first claim to get started'}
                        </p>
                        {!searchQuery && (
                          <button
                            onClick={() => navigate('/wizard-v2')}
                            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
                          >
                            Create New Claim
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ) : (
                  paginatedReports.map((report) => (
                    <tr
                      key={report.id}
                      className="hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => navigate(`/report/${report.id}`)}
                      role="link"
                      tabIndex={0}
                      aria-label={`View claim for ${report.client_name || 'Unnamed Claim'}`}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(`/report/${report.id}`); }}}
                    >
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <FileText className="w-5 h-5 text-blue-600" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{report.client_name || 'Unnamed Claim'}</p>
                            <p className="text-xs text-gray-500">{report.id.slice(0, 8)}...</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-4">
                        {report.law_firm ? (
                          <span className="inline-flex items-center gap-1.5 px-2 py-1 bg-gray-100 text-gray-700 rounded-md text-xs">
                            <Briefcase className="w-3 h-3" />
                            {report.law_firm}
                          </span>
                        ) : (
                          <span className="text-sm text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-5 py-4">
                        <span className="text-sm text-gray-600 capitalize">
                          {report.event_type || report.business_type || '-'}
                        </span>
                      </td>
                      <td className="px-5 py-4">
                        <span className="text-sm font-medium text-gray-900">
                          {report.result?.final_claim_amount 
                            ? formatCurrency(report.result.final_claim_amount)
                            : '-'
                          }
                        </span>
                      </td>
                      <td className="px-5 py-4">
                        {getStatusBadge(report.status)}
                      </td>
                      <td className="px-5 py-4">
                        <span className="text-sm text-gray-500">
                          {formatDate(report.created_at)}
                        </span>
                      </td>
                      <td className="px-5 py-4">
                        <div className="relative">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpenMenuId(openMenuId === report.id ? null : report.id);
                            }}
                            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                            data-testid={`report-menu-${report.id}`}
                            aria-label={`Actions for ${report.client_name || 'Unnamed Claim'}`}
                            aria-haspopup="true"
                            aria-expanded={openMenuId === report.id}
                          >
                            <MoreHorizontal className="w-4 h-4" aria-hidden="true" />
                          </button>
                          
                          {/* Dropdown Menu */}
                          {openMenuId === report.id && (
                            <div
                              className="absolute right-0 top-8 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50"
                              onClick={(e) => e.stopPropagation()}
                              role="menu"
                              aria-label="Report actions"
                            >
                              <button
                                onClick={() => {
                                  navigate(`/report/${report.id}`);
                                  setOpenMenuId(null);
                                }}
                                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                                data-testid={`report-view-${report.id}`}
                                role="menuitem"
                              >
                                <Eye className="w-4 h-4" aria-hidden="true" />
                                View Report
                              </button>
                              <button
                                onClick={() => {
                                  navigate(`/roadmap/${report.id}`);
                                  setOpenMenuId(null);
                                }}
                                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                                data-testid={`report-roadmap-${report.id}`}
                                role="menuitem"
                              >
                                <Map className="w-4 h-4" aria-hidden="true" />
                                View Roadmap
                              </button>
                              <button
                                onClick={async () => {
                                  setOpenMenuId(null);
                                  try {
                                    const backendUrl = process.env.REACT_APP_BACKEND_URL;
                                    const res = await fetch(`${backendUrl}/api/v1/download/report-pdf/${report.id}`);
                                    if (res.ok) {
                                      const blob = await res.blob();
                                      const url = window.URL.createObjectURL(blob);
                                      const a = document.createElement('a');
                                      a.href = url;
                                      a.download = `ClaimSync_Report_${report.client_name || report.id}.pdf`;
                                      document.body.appendChild(a);
                                      a.click();
                                      window.URL.revokeObjectURL(url);
                                      a.remove();
                                    } else {
                                      // Fallback: export dashboard data as CSV
                                      const csvData = `Client,Event Type,Status,Claim Amount\n${report.client_name || ''},${report.event_type || ''},${report.status || ''},${report.final_claim_amount || 0}`;
                                      const blob = new Blob([csvData], { type: 'text/csv' });
                                      const url = window.URL.createObjectURL(blob);
                                      const a = document.createElement('a');
                                      a.href = url;
                                      a.download = `ClaimSync_${report.client_name || report.id}.csv`;
                                      document.body.appendChild(a);
                                      a.click();
                                      window.URL.revokeObjectURL(url);
                                      a.remove();
                                    }
                                  } catch (err) {
                                    console.error('Export failed:', err);
                                  }
                                }}
                                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                                data-testid={`report-download-${report.id}`}
                                role="menuitem"
                              >
                                <Download className="w-4 h-4" aria-hidden="true" />
                                Export PDF
                              </button>
                              {canReleaseToAttorney && (
                                <button
                                  onClick={() => openReleaseModal(report.id)}
                                  className="w-full px-4 py-2 text-left text-sm text-blue-600 hover:bg-blue-50 flex items-center gap-2"
                                  data-testid={`report-release-${report.id}`}
                                  role="menuitem"
                                >
                                  <Scale className="w-4 h-4" aria-hidden="true" />
                                  Release to Attorney
                                </button>
                              )}
                              <hr className="my-1 border-gray-100" />
                              <button
                                onClick={() => {
                                  setShowReportDeleteConfirm(report.id);
                                  setOpenMenuId(null);
                                }}
                                className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                                data-testid={`report-delete-${report.id}`}
                                role="menuitem"
                              >
                                <Trash2 className="w-4 h-4" aria-hidden="true" />
                                Delete Report
                              </button>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Table Footer with Pagination */}
          {filteredReports.length > 0 && (
            <div className="px-5 py-3 border-t border-gray-200 bg-gray-50 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <div className="flex items-center gap-4">
                <p className="text-sm text-gray-500">
                  Showing {((currentPage - 1) * itemsPerPage) + 1} - {Math.min(currentPage * itemsPerPage, filteredReports.length)} of {filteredReports.length} claims
                </p>
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="px-2 py-1 text-sm border border-gray-200 rounded bg-white"
                  aria-label="Items per page"
                >
                  <option value={10}>10 per page</option>
                  <option value={25}>25 per page</option>
                  <option value={50}>50 per page</option>
                  <option value={100}>100 per page</option>
                </select>
              </div>
              <nav className="flex items-center gap-1" aria-label="Claims pagination">
                <button
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  className="px-2 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                  aria-label="Go to first page"
                >
                  First
                </button>
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                  aria-label="Go to previous page"
                >
                  Previous
                </button>
                
                {/* Page Numbers */}
                <div className="flex items-center gap-1 mx-2">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`px-3 py-1.5 text-sm rounded ${
                          currentPage === pageNum
                            ? 'bg-blue-600 text-white'
                            : 'text-gray-600 hover:bg-gray-100'
                        }`}
                        aria-label={`Page ${pageNum}`}
                        aria-current={currentPage === pageNum ? 'page' : undefined}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                  aria-label="Go to next page"
                >
                  Next
                </button>
                <button
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                  className="px-2 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                  aria-label="Go to last page"
                >
                  Last
                </button>
              </nav>
            </div>
          )}
        </div>

        {/* Report Delete Confirmation Modal */}
        {showReportDeleteConfirm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" role="dialog" aria-modal="true" aria-labelledby="delete-report-title">
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                  <Trash2 className="w-6 h-6 text-red-600" aria-hidden="true" />
                </div>
                <div>
                  <h3 id="delete-report-title" className="text-lg font-bold text-gray-900">Delete Report?</h3>
                  <p className="text-sm text-gray-500">This action cannot be undone</p>
                </div>
              </div>
              <p className="text-gray-600 mb-6">
                This will permanently delete this claim report and all associated data.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowReportDeleteConfirm(null)}
                  disabled={isDeletingReport}
                  className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors"
                  data-testid="report-delete-cancel"
                >
                  Cancel
                </button>
                <button
                  onClick={() => deleteReport(showReportDeleteConfirm)}
                  disabled={isDeletingReport}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors flex items-center justify-center gap-2"
                  data-testid="report-delete-confirm"
                >
                  {isDeletingReport ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4" />
                      Delete
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Release to Attorney Modal */}
        {showReleaseModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" role="dialog" aria-modal="true" aria-labelledby="release-attorney-title">
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">
              <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4">
                <div className="flex items-center gap-3">
                  <Scale className="w-6 h-6 text-white" aria-hidden="true" />
                  <h3 id="release-attorney-title" className="text-lg font-bold text-white">Release to Attorney</h3>
                </div>
              </div>
              
              <div className="p-6">
                {validationError && (
                  <p className="text-red-500 text-sm mb-4">{validationError}</p>
                )}
                <p className="text-gray-600 mb-4">
                  Select the law firm to release this case to. The attorney will be able to view and download the report.
                </p>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Law Firm
                  </label>
                  <select
                    value={selectedLawFirm}
                    onChange={(e) => { setSelectedLawFirm(e.target.value); setValidationError(null); }}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    data-testid="release-law-firm-select"
                  >
                    <option value="">Choose a law firm...</option>
                    {lawFirmsList.map((firm) => (
                      <option key={firm.id} value={firm.id} disabled={!firm.attorney_password_set}>
                        {firm.name} {firm.attorney_password_set ? '✓' : '(no portal access)'}
                      </option>
                    ))}
                  </select>
                  {lawFirmsList.length > 0 && !lawFirmsList.some(f => f.attorney_password_set) && (
                    <p className="text-xs text-amber-600 mt-2">
                      No law firms have attorney portal access set up. Go to Settings → Law Firm Management to enable access.
                    </p>
                  )}
                </div>
              </div>
              
              <div className="px-6 py-4 bg-gray-50 flex gap-3">
                <button
                  onClick={() => { setShowReleaseModal(null); setSelectedLawFirm(''); }}
                  disabled={isReleasing}
                  className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => releaseToAttorney(showReleaseModal)}
                  disabled={isReleasing || !selectedLawFirm}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="release-confirm-button"
                >
                  {isReleasing ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Releasing...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Release Case
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
